import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useAddKeyProp } from '../../utils/hooks';
import NavControl from '../NavControl';
import NavSubMenu from '../NavSubMenu';
import Link from '../Link';

const ns = `nav-menu`;

const NavMenu = ({menuItems, navigationText, currentPathname}) => {
	const [activeMenu, setActiveMenu] = useState(null);
	const [renderedMenuItems, setRenderedMenuItems] = useState();
	const menuContainerRef = useRef();
	const keyedMenuItems = useAddKeyProp(menuItems, 'burnham');

	// Add ref properties to internal state (array of menu items).
	useEffect(() => {
		setRenderedMenuItems(keyedMenuItems?.map(item => {
			return {
				menuItemRef: React.createRef(),
				subMenuRef: React.createRef(),
				...item,
			};
		}));
	}, [keyedMenuItems]);

	const rootClassnames = classNames({
		[`${ ns }`]: true,
	});

	const menuContainerClassnames = classNames({
		[`${ ns }__menu-container`]: true,
		[`${ ns }__menu-container--open`]: !!activeMenu,
	});

	/** Ref callback setters */
	const setSubMenuRef = (key, node) => {
		renderedMenuItems.forEach(item => {
			if (item.key === key && !item.subMenuRef.current) {
				// eslint-disable-next-line no-param-reassign
				item.subMenuRef.current = node;
			}
		});
	};

	const setMenuItemRef = (key, node) => {
		renderedMenuItems.forEach(item => {
			if (item.key === key && !item.menuItemRef.current) {
				// eslint-disable-next-line no-param-reassign
				item.menuItemRef.current = node;
			}
		});
	};

	/** Menu management helpers */
	const updateMenuContainerHeight = height => {
		if (menuContainerRef.current) {
			(height && (menuContainerRef.current.style.height = height));
		}
	};

	const activateMenu = menuKey => {
		const updatedItem = (
			renderedMenuItems.filter(item => {
				return item.key === menuKey;
			})[0]
		);

		if (
			updatedItem
			&& updatedItem.menuItemRef?.current
			&& updatedItem.subMenuRef?.current
		) {
			// Get height of new active menu.
			const { height } = updatedItem.subMenuRef.current.getBoundingClientRect();

			// Set containers new height.
			// eslint-disable-next-line
			updateMenuContainerHeight(`${ height }px`);
		}

		setActiveMenu(menuKey);
	};

	const deactivateMenu = () => {
		updateMenuContainerHeight(0);
		setActiveMenu(null);
	};

	const onClickHandler = menuKey => {
		if (activeMenu) {
			updateMenuContainerHeight(0);
			setActiveMenu(null);
		} else {
			setActiveMenu(menuKey);
		}
	};

	return (
		<nav className={rootClassnames} role={`navigation`}>
			{/* Nav Menu Control Items */}
			<div className={`${ ns }__menu-controls`}>
				{
					renderedMenuItems
					&& renderedMenuItems.map(item => {
						return (
							(
								item.dropdown?.column
								&& (
									<NavControl
										active={item.key === activeMenu}
										setMenuItemRef={node => {
											setMenuItemRef(item.key, node);
										}}
										key={`menu-control-${ item.key }`}
										label={item.label}
										onClick={() => {
											onClickHandler(item.key);
										}}
										onMouseEnter={() => {
											activateMenu(item.key);
										}}
										onMouseLeave={() => {
											deactivateMenu();
										}}
									/>
								)
							)
							|| (
								!item.dropdown?.links
								&& item.optionalUrl
								&& (
									<div
										key={item.key}
										className={`nav-control`}
									>
										<Link
											to={item.optionalUrl}
											className={`text-large`}
										>
											{item.label}
										</Link>
									</div>
								)
							)
						);
					})
				}
			</div>

			{/* Render menu wrapper with sub menus */}
			<div
				ref={menuContainerRef}
				className={menuContainerClassnames}
			>
				{
					renderedMenuItems
					&& renderedMenuItems.map(item => {
						return (
							item.dropdown?.column
							&& (
								<NavSubMenu
									setSubMenuRef={node => {
										setSubMenuRef(item.key, node);
									}}
									key={`sub-menu-${ item.key }`}
									menuItems={item.dropdown?.column}
									navigationText={navigationText}
									active={activeMenu === item.key}
									onMouseEnter={() => {
										activateMenu(item.key);
									}}
									onMouseLeave={() => {
										deactivateMenu();
									}}
									currentPathname={currentPathname}
								/>
							)
						);
					})
				}
			</div>
		</nav>
	);
};

export default NavMenu;
